import { useContext, useEffect, useState, useCallback } from "react";
import { useParams } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { doc, getDoc, collection, getDocs, query, where } from "firebase/firestore";
import toast from "react-hot-toast";

import MyContext from "../../context/Context";
import { fireDB } from "../../firebase/FirebaseConfig";
import Loader from "../../components/Loader/Loader";
import { ADD } from '../../redux/Actions/action';

const Product = () => {
    const { loading, setLoading } = useContext(MyContext);
    const [product, setProduct] = useState(null);
    const [deliveredProducts, setDeliveredProducts] = useState({});
    const { id } = useParams();
    const [selectedSize, setSelectedSize] = useState(""); // ✅ New state
    const [selectedColor, setSelectedColor] = useState(""); // ✅ New color state
    const cartItems = useSelector(state => state.cartreducer.carts);
    const dispatch = useDispatch();


    const sizeOptions = [
        { label: "CUBEE  - Length x Width x Height 6x6x6", value: "C01", price: 495 },
        { label: "CUBEE  - Length x Width x Height 9x9x9", value: "C02", price: 849 },
        { label: "CUBEE  - Length x Width x Height 11x11x11", value: "C03", price: 1999 },
        { label: "CUBEE - Length x Width x Height 15x15x15", value: "C04", price: 2980 },
        { label: "TUBO  -  Length x Width x Height 0x10x12", value: "T01", price: 1385 },
        { label: "TUBO  -  Length x Width x Height 0x13x17", value: "T02", price: 2255 },
        { label: "WIDEE  - Length x Width x Height 0x11x11", value: "W01", price: 1375 },
        { label: "WIDEE  - Length x Width x Height 0x13x13", value: "W02", price: 1485 },
        { label: "WIDEE  - Length x Width x Height 0x15x15", value: "W03", price: 2255 },
        { label: "WIDEE  - Length x Width x Height 0x17x17", value: "W04", price: 3350 },
        { label: "TRIYA  - Length x Width x Height 11x11x11", value: "TR01", price: 1499 },
        { label: "TRIYA - Length x Width x Height 0x14x13", value: "TR02", price: 1865 },
        { label: "TRIYA - Length x Width x Height 0x17x14.5", value: "TR03", price: 3160 },
        { label: "TRIYA - Length x Width x Height 0x20x16", value: "TR04", price: 4375 },
        { label: "BOXA  -   Length x Width x Height 24x8x0", value: "B01", price: 2075 },
        { label: "BOXA  - Length x Width x Height 30x12x0", value: "B02", price: 4450 },
        { label: "BOXA  - Length x Width x Height 36x12x0", value: "B03", price: 5485 },
        { label: "OVALIN  - Length x Width x Height 24x14x12", value: "OV01", price: 2000 },  // false price 
        { label: "OVALIN  - Length x Width x Height 18x11x10", value: "OV02", price: 3000 }, // false price 
        { label: "BONSAI  - Length x Width x Height 0x18x7.6", value: "BON01", price: 2000 }, // false price 
        { label: "BONSAI  - Length x Width x Height 0x24x10", value: "BON02", price: 3000 }, // false price 
        { label: "BONSAI  - Length x Width x Height 0x36x170", value: "BON03", price: 4000 }, // false price 
    ];

    const addCart = useCallback((item) => {
        dispatch(ADD(item));
        toast.success("Added to cart");
    }, [dispatch]);

    const getProductData = async () => {
        setLoading(true);
        try {
            const productDoc = await getDoc(doc(fireDB, "products", id));
            if (productDoc.exists()) {
                setProduct({ ...productDoc.data(), id: productDoc.id });
            } else {
                toast.error("Product not found");
            }
        } catch (error) {
            console.error(error);
            toast.error("Error fetching product");
        } finally {
            setLoading(false);
        }
    };

    const getDeliveredOrders = async () => {
        try {
            const q = query(collection(fireDB, "order"), where("status", "==", "delivered"));
            const querySnapshot = await getDocs(q);

            const deliveredMap = {};
            querySnapshot.forEach(doc => {
                const orderData = doc.data();
                orderData.cartItems?.forEach(item => {
                    deliveredMap[item.id] = true;
                });
            });

            setDeliveredProducts(deliveredMap);
        } catch (error) {
            console.error(error);
            toast.error("Failed to load delivered orders");
        }
    };

    useEffect(() => {
        window.scrollTo(0, 0);
        getProductData();
        getDeliveredOrders();
    }, []);

    useEffect(() => {
        localStorage.setItem('cart', JSON.stringify(cartItems));
    }, [cartItems]);

    const isSold = deliveredProducts[id];
    const potSizes = ["Small", "Medium", "Large"]; // ✅ Available sizes
    const potColors = ["Grey Stone", "Marble White", "Sandstone", "Mosaic Grey"]; // ✅ Colors
    // ✅ Early return for loader
    if (loading || !product) {
        return (
            <div className="flex justify-center items-center h-[80vh]">
                <Loader />
            </div>
        );
    }

    const { productImageUrl, title, price, description, sizeCode } = product;

    return (
        <section className="py-5 lg:py-16 font-poppins">
            <div className="max-w-6xl h-full px-4 mx-auto">
                <div className="flex flex-wrap mb-24 -mx-4">
                    {/* Image Section */}
                    <div className="w-full flex justify-center items-center px-4 mb-8 md:w-1/2 md:mb-0">
                        {typeof productImageUrl === 'string' ? (
                            <img
                                className="object-cover p-2 border rounded-md"
                                src={productImageUrl}
                                alt="Product"
                            />
                        ) : (
                            <div className="flex space-x-2 overflow-x-auto">
                                <div>
                                    {/* Main Image */}
                                    {productImageUrl?.[0] && (
                                        <a href={productImageUrl[0]} target="_blank" rel="noopener noreferrer">
                                            <img
                                                src={productImageUrl[0]}
                                                alt="Product"
                                                loading="lazy"
                                                className="object-cover p-2 border rounded-md mb-4"
                                            />
                                        </a>
                                    )}
                                    {/* More Images */}
                                    <div className="flex  md:space-x-2 overflow-x-auto">
                                        {productImageUrl?.slice(1).map((url, i) => (
                                            <a href={url} target="_blank" rel="noopener noreferrer" key={i}>
                                                <img
                                                    src={url}
                                                    alt={`Product ${i}`}
                                                    loading="lazy"
                                                    className=" md:h-32  h-24 p-2 object-cover border rounded-md"
                                                />
                                            </a>
                                        ))}
                                        <br />

                               

                                    </div>
                                    <br />
                                    <a href={"/pots/pot-colors.jpg"} target="_blank" rel="noopener noreferrer">
                                            <p className="text-lg font-semibold">Available Colors</p>
                                            <img
                                                src="/pots/pot-colors.jpg"
                                                alt="pots-colors"
                                                loading="lazy"
                                                className="w-56 h-48  p-2 border rounded-md"
                                            />
                                        </a>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Details Section */}
                    <div className="w-full px-4 md:w-1/2">
                        <div className="lg:pl-20">
                            <div className="mb-6">
                                <h2 className="text-2xl md:text-4xl font-semibold tracking-wide mb-4">
                                    {title}
                                </h2>

                                {/* Stars */}
                                <div className="flex mb-4 text-yellow-500">
                                    {[...Array(4)].map((_, i) => (
                                        <i key={i} className="fa-solid fa-star"></i>
                                    ))}
                                    <i className="fa-solid fa-star-half-stroke"></i>
                                </div>

                                <select
                                    value={product.sizeCode || ''}
                                    onChange={(e) => {
                                        const selected = sizeOptions.find(item => item.value === e.target.value);
                                        if (selected) {
                                            setProduct(prev => ({
                                                ...prev,
                                                sizeCode: selected.value,
                                                price: selected.price
                                            }));
                                        }
                                    }}
                                    className="input-field mt-4 mb-4"
                                >
                                    <option disabled value="">Select Size</option>
                                    {sizeOptions
                                        .filter(option => option.label.toLowerCase().includes(title.toLowerCase()))
                                        .map(option => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                </select>

                                <p className="text-xl font-semibold">₹{product.price}</p>
                                {/* Price */}
                                {/* <p className="text-2xl md:text-3xl font-semibold text-black mt-2">
                                    ₹ {price}
                                </p> */}
                            </div>

                            {/* Description */}
                            <div className="mb-6">
                                <h2 className="text-xl font-semibold">Highlights</h2>
                                <p className="text-lg font-medium">{description}</p>
                            </div>
                            {/* ✅ Size Selector */}
                            {/* <div className="mb-6">
                                <label htmlFor="size" className="block text-lg font-semibold mb-2">Select Pot Size:</label>
                                <select
                                    id="size"
                                    value={selectedSize}
                                    onChange={(e) => setSelectedSize(e.target.value)}
                                    className="md:w-48 w-36 md:px-4 py-2 px-2 text-sm  border rounded-md"
                                >
                                    <option value="">-- Choose Size --</option>
                                    {potSizes.map((size) => (
                                        <option key={size} value={size}>{size}</option>
                                    ))}
                                </select>
                            </div> */}

                            {/* ✅ Pot Color Selector */}
                            <div className="mb-6">
                                <p className="block text-lg font-semibold mb-2">Choose Pot Color:</p>
                                <div className="flex space-x-4">
                                    {[
                                        { name: "Grey Stone", color: "#7d7d7d" },
                                        { name: "Marble White", color: "#f0f0f0" },
                                        { name: "Sandstone", color: "#d2b48c" },
                                        { name: "Mosaic Grey", color: "#a9a9a9" },
                                    ].map((option) => (
                                        <div
                                            key={option.name}
                                            onClick={() => setSelectedColor(option.name)}
                                            className={`md:w-16 md:h-16 h-12 w-12 rounded-full cursor-pointer border-2 flex items-center justify-center transition-all duration-200 ${selectedColor === option.name ? "border-black scale-105" : "border-gray-300"
                                                }`}
                                            style={{ backgroundColor: option.color }}
                                            title={option.name}
                                        >

                                            {selectedColor === option.name && (
                                                <i className="fa-solid fa-check text-black"></i>
                                            )}

                                        </div>
                                    ))}
                                </div>
                            </div>


                            {/* Add to Cart Button */}
                            <div className="mb-6">
                                <button
                                    type="button"
                                    onClick={() => {
                                        // if (!selectedSize) {
                                        //     toast.error("Please select a pot size");
                                        //     return;
                                        // }
                                        if (!selectedColor) {
                                            toast.error("Please select a pot color");
                                            return;
                                        }
                                        addCart({ ...product, sizeCode, selectedColor });
                                    }}
                                    className={`w-full  rounded-lg font-semibold transition 
                                        ${isSold ? "cart-btn cursor-not-allowed" : "cart-btn text-white"}`}
                                    disabled={isSold}
                                >
                                    {isSold ? "Sold" : "Add to Cart"}
                                </button>
                            </div>

                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Product;
